import React, { useEffect, useState } from "react"

import { useHistory } from "react-router-dom"
import { useSelector } from "react-redux"

import { fetchProfileWithTags } from "@actions/userActions"
import PeopleAvatar from "@components/PeopleAvatar"
import { selectUserId } from "@selectors/user"
import { images } from "@utils/imageUtils"
import { userContactTypes } from "@utils/contactUtils"
import {
  fetchPersonMechanismsMerged,
  isDevBackend,
} from "@utils/devBackendShim"

import { usePersonCardStore } from "../../stores/personCardStore"

import "./index.scss"

// PERSON-CARD (T2, 2026-07-06 -- deterministic-interaction-ingestion-plan-2026-07-04.md §0-GOAL):
// "click any avatar anywhere in the product ... and the contact details for email, Slack,
// mobile, and LinkedIn are MERGED and available on one card ... One current-best mechanism per
// channel type, provenance-chipped, two-layer rules applied." Opened via usePersonCardStore
// (mounted once at App root, DEV-WEB-STANDING-RULES 9: gated on isAuthenticatedUser so it never
// touches the pre-auth render) and triggered today from the People Directory avatar click
// (Directory.js) -- the same store lets any future avatar surface (TeeUp participants, inbox,
// map) open it with one `usePersonCardStore.getState().open(personId)` call, no prop drilling.
//
// TWO-LAYER DISPLAY CONTRACT (plan §4-PROVENANCE, QJ design ruling 2026-07-04): "their profile"
// (assertion_class=self_published, the person's own public CooWe profile fields -- already live,
// GET /users/{id} via fetchProfileWithTags, same data ProfilePagePublicInfo renders) is ALWAYS
// rendered separately from "From your history" (shared_with_viewer | viewer_private -- mined
// per-channel current-best mechanisms with provenance). The two layers are NEVER merged into one
// value per field; a field present in one layer only renders in that layer only; a conflict
// between layers renders BOTH with chips, never a silently-picked winner.
//
// MINED-LAYER ENDPOINT STATUS (2026-07-06 4-EXP, rewired): `fetchPersonMechanismsMerged`
// (devBackendShim.js) now calls the REAL, LIVE endpoint -- GET /v1/users/me/contacts/mechanisms
// (contact-mechanism-harvest track, viewer-scoped "me", `counterpart_user_id`/`counterpart_key`)
// -- and reshapes its per-channel mechanism arrays into one current-best MechRow per channel plus
// any genuinely different value as a `conflicts[]` entry. Still never throws; a 404/network/parse
// error resolves `{ok:false}` here, rendered as a true, honest empty state -- never a fabricated
// mechanism (real-empty-state law).
const CHANNEL_META = {
  email: { label: "Email", icon: images.ndProfilePublicInfoEmail },
  mobile: { label: "Mobile", icon: images.ndProfilePublicInfoPhone },
  slack: { label: "Slack", icon: images.icon_slack_enabled || images.ndCreateTeeupInviteLink },
  linkedin: { label: "LinkedIn", icon: images.ndCreateTeeupInviteLink },
}

const PUBLIC_ROW_TYPES = [
  { channel: "email", types: [userContactTypes.EMAIL, userContactTypes.PRIMARY_EMAIL] },
  { channel: "mobile", types: [userContactTypes.PHONE, userContactTypes.PRIMARY_PHONE] },
]

const assertionLabel = tier => {
  if (tier === "self_published") return "their profile"
  if (tier === "shared_with_viewer") return "shared with you"
  if (tier === "viewer_private") return "your records"
  return null
}

const ProvenanceChip = ({ mech }) => {
  const tier = assertionLabel(mech?.assertion_class)
  const bits = [tier, mech?.source, mech?.seen_at].filter(Boolean)
  if (bits.length === 0) return null
  return (
    <span className="person-card__chip" data-testid="person-card-provenance-chip">
      {bits.join(" · ")}
    </span>
  )
}

const PersonCard = () => {
  const openPersonId = usePersonCardStore(s => s.openPersonId)
  const close = usePersonCardStore(s => s.close)
  const history = useHistory()
  const currentUserId = useSelector(selectUserId)

  const [profile, setProfile] = useState(null)
  const [profileLoading, setProfileLoading] = useState(false)
  const [mined, setMined] = useState({ ok: false })
  const [minedLoading, setMinedLoading] = useState(false)

  useEffect(() => {
    if (!openPersonId) {
      setProfile(null)
      setMined({ ok: false })
      return
    }
    let cancelled = false
    setProfileLoading(true)
    setMinedLoading(true)
    fetchProfileWithTags(openPersonId, currentUserId).then(data => {
      if (!cancelled) {
        setProfile(data || null)
        setProfileLoading(false)
      }
    })
    fetchPersonMechanismsMerged(openPersonId).then(result => {
      if (!cancelled) {
        setMined(result)
        setMinedLoading(false)
      }
    })
    return () => {
      cancelled = true
    }
  }, [openPersonId, currentUserId])

  if (!openPersonId) return null

  const person = profile?.profile || {}
  const publicMechanisms = profile?.contactMechanisms || []
  const publicRows = PUBLIC_ROW_TYPES.map(row => {
    const match = publicMechanisms.find(item =>
      row.types.includes(item.typeId ?? item.typeid)
    )
    return match ? { ...row, value: match.value } : null
  }).filter(Boolean)

  const minedChannels = mined.ok ? mined.data?.channels || {} : {}
  const minedConflicts = mined.ok ? mined.data?.conflicts || [] : []
  const hasMinedData =
    Object.keys(minedChannels).length > 0 || minedConflicts.length > 0

  const goToFullProfile = () => {
    close()
    history.push(`/profile/${openPersonId}`)
  }

  return (
    <div
      className="person-card__backdrop"
      data-testid="person-card-backdrop"
      onClick={close}>
      <div
        className="person-card"
        data-testid="person-card"
        onClick={e => e.stopPropagation()}>
        <button
          type="button"
          className="person-card__close"
          data-testid="person-card-close"
          onClick={close}
          aria-label="Close">
          &times;
        </button>

        {profileLoading && !profile ? (
          <div className="person-card__loading" data-testid="person-card-loading">
            Loading…
          </div>
        ) : (
          <>
            <div className="person-card__header">
              <PeopleAvatar
                avatar={person.avatar}
                name={person.name}
                className="person-card__avatar"
                fallbackClassName="person-card__avatar-fallback"
              />
              <div className="person-card__title">
                <h3 className="person-card__name">{person.name || "Unknown"}</h3>
                {!!person.username && (
                  <h4 className="person-card__username">@{person.username}</h4>
                )}
              </div>
            </div>

            <div className="person-card__section" data-testid="person-card-their-profile">
              <h5 className="person-card__section-title">Their profile</h5>
              {publicRows.length > 0 ? (
                <div className="person-card__rows">
                  {publicRows.map(row => (
                    <div
                      className="person-card__row"
                      key={row.channel}
                      data-testid={`person-card-public-${row.channel}`}>
                      <span className="person-card__row-label">
                        {CHANNEL_META[row.channel]?.label}
                      </span>
                      <span className="person-card__row-value">{row.value}</span>
                      <span className="person-card__chip">their profile</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div
                  className="person-card__empty"
                  data-testid="person-card-public-empty">
                  No public contact info on their profile
                </div>
              )}
            </div>

            <div className="person-card__section" data-testid="person-card-your-history">
              <h5 className="person-card__section-title">From your history</h5>
              {minedLoading ? (
                <div
                  className="person-card__skeleton"
                  data-testid="person-card-history-loading">
                  <div className="person-card__skeleton-row" />
                  <div className="person-card__skeleton-row" />
                </div>
              ) : hasMinedData ? (
                <div className="person-card__rows">
                  {Object.entries(minedChannels).map(([channel, mech]) => (
                    <div
                      className="person-card__row"
                      key={channel}
                      data-testid={`person-card-mined-${channel}`}>
                      <span className="person-card__row-label">
                        {CHANNEL_META[channel]?.label || channel}
                      </span>
                      <span className="person-card__row-value">{mech?.value}</span>
                      {mech?.current_best && (
                        <span className="person-card__badge">current best</span>
                      )}
                      <ProvenanceChip mech={mech} />
                    </div>
                  ))}
                  {minedConflicts.map((mech, idx) => (
                    <div
                      className="person-card__row person-card__row--conflict"
                      key={`conflict-${idx}`}
                      data-testid={`person-card-conflict-${idx}`}>
                      <span className="person-card__row-label">
                        {CHANNEL_META[mech?.channel]?.label || mech?.channel}
                      </span>
                      <span className="person-card__row-value">{mech?.value}</span>
                      <span className="person-card__badge person-card__badge--conflict">
                        conflicting
                      </span>
                      <ProvenanceChip mech={mech} />
                    </div>
                  ))}
                </div>
              ) : (
                <div
                  className="person-card__empty"
                  data-testid="person-card-history-empty">
                  {isDevBackend()
                    ? "No mined contact history yet"
                    : "Contact-history mining is available on the dev backend only"}
                </div>
              )}
            </div>

            <button
              type="button"
              className="person-card__full-profile"
              data-testid="person-card-view-profile"
              onClick={goToFullProfile}>
              View full profile
            </button>
          </>
        )}
      </div>
    </div>
  )
}

export default PersonCard
